import type { Affiliation } from '@interfaces/affiliation';

import type { SxProps, Theme } from '@mui/material';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
} from '@ovComponents/2-component/table/table';
import { colors } from '@ovComponents/legacyColor';
import { generateClientNameString } from '@utils/generateClientNameString';
import { IconButton } from 'hephaestus/web';
import { Delete as DeleteIcon } from 'hephaestus/webIcons';
import type { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';

const tableHeadSx: SxProps<Theme> = {
  backgroundColor: '#F0F4F8',
  borderRadius: '2px',
  border: 'none',
  color: colors.gray500,
};

type AffiliationTableProps = {
  affiliations?: Affiliation[];
  onDelete?: (affiliation: Affiliation) => void;
  showType?: boolean;
  showRelation?: boolean;
  showAllocation?: boolean;
  allowDelete?: boolean;
};

export const AffiliationTable = (props: AffiliationTableProps) => {
  const { t } = useTranslation([
    'workflowCompletions',
    'affiliationRelations',
    'affiliationTypes',
  ]);
  const getHandleDeleteFn = (affiliation: Affiliation) => () =>
    props.onDelete?.(affiliation);
  const affiliations = props?.affiliations ?? [];
  const numSecondaryColumns = [
    props.showType,
    props.showRelation,
    props.showAllocation,
  ].filter(Boolean).length;
  const secondaryColumnWidth = Math.min(60 / (numSecondaryColumns || 1), 30);
  const DeleteButton = ({
    onClick,
    sx,
  }: {
    onClick?: () => void;
    sx?: ComponentProps<typeof IconButton>['sx'];
  }) => (
    <IconButton onClick={onClick} sx={sx} aria-label='delete' size='xsmall'>
      <DeleteIcon fontSize='inherit' />
    </IconButton>
  );

  return affiliations.length > 0 ? (
    <Table size='small' aria-label='table'>
      <TableHead sx={tableHeadSx}>
        <TableRow>
          <TableHeadCell>
            {t(
              'workflowCompletions:baseAccountCreation.affiliationTable.nameHeader',
            )}
          </TableHeadCell>
          {props.showType && (
            <TableHeadCell sx={{ width: `${secondaryColumnWidth}%` }}>
              {t(
                'workflowCompletions:baseAccountCreation.affiliationTable.typeHeader',
              )}
            </TableHeadCell>
          )}
          {props.showRelation && (
            <TableHeadCell sx={{ width: `${secondaryColumnWidth}%` }}>
              {t(
                'workflowCompletions:baseAccountCreation.affiliationTable.relationHeader',
              )}
            </TableHeadCell>
          )}
          {props.showAllocation && (
            <TableHeadCell sx={{ width: `${secondaryColumnWidth}%` }}>
              {t(
                'workflowCompletions:baseAccountCreation.affiliationTable.allocationHeader',
              )}
            </TableHeadCell>
          )}
          {props.allowDelete && (
            <TableHeadCell padding='checkbox'>
              <DeleteButton sx={{ visibility: 'hidden' }} />
            </TableHeadCell>
          )}
        </TableRow>
      </TableHead>
      <TableBody>
        {affiliations.map((affiliation, idx) => (
          <TableRow hover key={idx}>
            <TableCell>{generateClientNameString(affiliation.user)}</TableCell>
            {props.showType && (
              <TableCell sx={{ width: `${secondaryColumnWidth}%` }}>
                {t(`affiliationTypes:${affiliation.type}`)}
              </TableCell>
            )}
            {props.showRelation && (
              <TableCell sx={{ width: `${secondaryColumnWidth}%` }}>
                {t(
                  `affiliationTypes:affiliationRelationship.${affiliation.relation}`,
                )}
              </TableCell>
            )}
            {props.showAllocation && (
              <TableCell sx={{ width: `${secondaryColumnWidth}%` }}>
                {`${affiliation.allocation ?? 0}%`}
              </TableCell>
            )}
            {props.allowDelete && (
              <TableCell padding='checkbox'>
                <DeleteButton onClick={getHandleDeleteFn(affiliation)} />
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  ) : (
    <></>
  );
};
